import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { bibleNotesRequest, noteReference } from './bibleNotesApi';
import { useModal } from '../ModalContext';
import { TranslateLanguage } from './KeywordTranslator';
import './bibleNotes.css';
import './bibleNoteControls.css';

const plainVerseText = value => {
  const element = document.createElement('div');
  element.innerHTML = String(value || '');
  return (element.textContent || '').replace(/\s+/g, ' ').trim();
};

export default function BibleNoteDialog({ book, bookName, chapter, startVerse = 1, endVerse, maxVerse, scriptureText = '', chapterVerses, initialNote, onClose }) {
  const navigate = useNavigate();
  const { setLoginModalVisible } = useModal();
  const language = localStorage.userLanguage || ((navigator.language || navigator.userLanguage) === 'zh-CN' ? 'S' : 'T');
  const translate = text => TranslateLanguage(language, text);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(initialNote?.id || null);
  const [start, setStart] = useState(initialNote?.startVerse || startVerse);
  const [end, setEnd] = useState(initialNote?.endVerse || endVerse || startVerse);
  const [title, setTitle] = useState(initialNote?.title || '');
  const [content, setContent] = useState(initialNote?.content || '');
  const [tags, setTags] = useState(initialNote?.tags || '');
  const [saving, setSaving] = useState(false);
  const [verses, setVerses] = useState([]);
  const [loadingVerses, setLoadingVerses] = useState(true);
  const [openNoteMenuId, setOpenNoteMenuId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoadingVerses(true);
    if (Array.isArray(chapterVerses) && chapterVerses.length) {
      setVerses(chapterVerses.map(item => ({ verse: Number(item.verse), text: plainVerseText(item.text) })));
      setLoadingVerses(false);
      return () => { cancelled = true; };
    }
    fetch(`/api/bible/verses?version=unv&chineses=${encodeURIComponent(book)}&chap=${encodeURIComponent(chapter)}`)
      .then(response => { if (!response.ok) throw new Error('經文載入失敗'); return response.json(); })
      .then(data => {
        if (!cancelled) setVerses((data.record || [])
          .map(item => ({ verse: Number(item.sec), text: plainVerseText(item.bible_text) }))
          .filter(item => Number.isInteger(item.verse) && item.verse > 0));
      })
      .catch(() => { if (!cancelled) setVerses([]); })
      .finally(() => { if (!cancelled) setLoadingVerses(false); });
    return () => { cancelled = true; };
  }, [book, chapter, chapterVerses]);

  const selectedVerses = verses.filter(item => item.verse >= Number(start) && item.verse <= Number(end));
  const selectedText = selectedVerses.map(item => `${item.verse} ${item.text}`).join('\n').slice(0, 4000);
  const lastVerse = verses.length ? verses[verses.length - 1].verse : (maxVerse || 200);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ book, chapter: String(chapter), pageSize: '100' });
      const data = await bibleNotesRequest(`?${params}`);
      setNotes(data.items || []);
      setError('');
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [book, chapter]);
  useEffect(() => {
    const onKey = event => {
      if (event.key !== 'Escape') return;
      if (openNoteMenuId !== null) setOpenNoteMenuId(null);
      else onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, openNoteMenuId]);
  useEffect(() => {
    if (openNoteMenuId === null) return undefined;
    const closeOutside = event => {
      if (!event.target.closest('.bible-note-existing .bible-notes-card-menu')) setOpenNoteMenuId(null);
    };
    document.addEventListener('pointerdown', closeOutside);
    return () => document.removeEventListener('pointerdown', closeOutside);
  }, [openNoteMenuId]);

  const edit = note => {
    setOpenNoteMenuId(null);
    setEditingId(note.id);
    setStart(note.startVerse);
    setEnd(note.endVerse);
    setTitle(note.title || '');
    setContent(note.content);
    setTags(note.tags || '');
  };
  const reset = () => {
    setEditingId(null);
    setStart(startVerse);
    setEnd(endVerse || startVerse);
    setTitle('');
    setContent('');
    setTags('');
  };
  const save = async event => {
    event.preventDefault();
    if (!content.trim()) { setError(translate('請寫下筆記內容。')); return; }
    if (Number(end) < Number(start)) { setError(translate('結束節數不能小於起始節數。')); return; }
    setSaving(true);
    try {
      const payload = {
        book, bookName: bookName || book, chapter: Number(chapter),
        startVerse: Number(start), endVerse: Number(end),
        scriptureText: selectedText || (editingId ? (notes.find(n => n.id === editingId)?.scriptureText || initialNote?.scriptureText || '') : scriptureText),
        title: title.trim(), content: content.trim(), tags: tags.trim(),
      };
      await bibleNotesRequest(editingId ? `/${editingId}` : '', {
        method: editingId ? 'PUT' : 'POST', body: JSON.stringify(payload),
      });
      reset();
      await load();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };
  const remove = async note => {
    if (!window.confirm(translate(`刪除 ${noteReference(note)} 的這篇筆記？`))) return;
    try {
      await bibleNotesRequest(`/${note.id}`, { method: 'DELETE' });
      setOpenNoteMenuId(null);
      if (editingId === note.id) reset();
      await load();
    } catch (e) { setError(e.message); }
  };

  return createPortal(
    <div className="bible-note-overlay" onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="bible-note-dialog" role="dialog" aria-modal="true" aria-label={translate('個人聖經筆記')}>
        <header className="bible-note-dialog-header">
          <div><strong>{translate('個人聖經筆記')}</strong><small>{translate(bookName || book)} {chapter} {translate('章')}</small></div>
          <button type="button" onClick={onClose} aria-label={translate('關閉')}>×</button>
        </header>
        {!sessionStorage.getItem('token') ? <div className="bible-note-dialog-body"><p>{translate('請先登入，才能保存及查看個人聖經筆記。')}</p><button type="button" onClick={() => { onClose(); setLoginModalVisible(true); }}>{translate('登入')}</button></div> : <div className="bible-note-dialog-body">
          <form onSubmit={save} className="bible-note-form">
            <div className="bible-note-range-picker">
              <strong>{translate('經節範圍')}</strong>
              <small>{translate(loadingVerses ? '正在載入本章經文…' : verses.length ? `本章共 ${verses.length} 節 · 已選 ${selectedVerses.length} 節` : '無法載入本章經文，請輸入節數')}</small>
              <div className="bible-note-range">
                <label>{translate('從')}
                  {verses.length ? <select aria-label={translate('起始經節')} value={start} onChange={e => {
                    const next = Number(e.target.value);
                    setStart(next);
                    if (Number(end) < next) setEnd(next);
                  }}>{verses.map(item => <option key={item.verse} value={item.verse}>{item.verse} · {translate(item.text.slice(0, 32))}</option>)}</select>
                    : <input aria-label={translate('起始節')} type="number" min="1" max={lastVerse} value={start} onChange={e => setStart(e.target.value)} required />}
                </label>
                <label>{translate('至')}
                  {verses.length ? <select aria-label={translate('結束經節')} value={end} onChange={e => setEnd(Number(e.target.value))}>{verses.filter(item => item.verse >= Number(start)).map(item => <option key={item.verse} value={item.verse}>{item.verse} · {translate(item.text.slice(0, 32))}</option>)}</select>
                    : <input aria-label={translate('結束節')} type="number" min="1" max={lastVerse} value={end} onChange={e => setEnd(e.target.value)} required />}
                </label>
              </div>
            </div>
            {verses.length ? <div className="bible-note-passage-preview">
              <strong>{translate('所選經文')}</strong>
              <div>{selectedVerses.map(item => <p key={item.verse}><b>{item.verse}</b> {translate(item.text)}</p>)}</div>
              <details><summary>{translate(`查看本章全部經文（${verses.length} 節）`)}</summary>
                <div className="bible-note-all-verses">{verses.map(item => <button type="button" key={item.verse} onClick={() => {
                  if (item.verse < Number(start)) setStart(item.verse);
                  setEnd(item.verse);
                }}>{item.verse}　{translate(item.text)}</button>)}</div>
              </details>
            </div> : (scriptureText || initialNote?.scriptureText) && <blockquote>{translate(initialNote?.scriptureText || scriptureText)}</blockquote>}
            <label>{translate('標題（選填）')}<input value={title} maxLength="200" onChange={e => setTitle(e.target.value)} placeholder={translate('例如：今天的領受')} /></label>
            <label>{translate('筆記')}<textarea value={content} maxLength="20000" onChange={e => setContent(e.target.value)} rows="5" placeholder={translate('寫下你的觀察、心得或禱告……')} required /></label>
            <label>{translate('標籤（選填）')}<input value={tags} maxLength="500" onChange={e => setTags(e.target.value)} placeholder={translate('例如：禱告、盼望')} /></label>
            {error && <p className="bible-note-error" role="alert">{translate(error)}</p>}
            <div className="bible-note-actions">
              {editingId && <button type="button" onClick={reset}>{translate('取消編輯')}</button>}
              <button type="submit" className="primary" disabled={saving}>{translate(saving ? '保存中…' : editingId ? '保存修改' : '保存筆記')}</button>
            </div>
          </form>
          <div className="bible-note-existing">
            <h3>{translate('本章筆記')} {notes.length ? `(${notes.length})` : ''}</h3>
            {loading ? <p>{translate('載入中…')}</p> : !notes.length ? <p className="muted">{translate('本章還沒有筆記。')}</p> : notes.map(note => (
              <article key={note.id}>
                <div className="bible-notes-card-menu">
                  <button type="button" className="bible-notes-menu-trigger" aria-label={translate(`${noteReference(note)} 更多操作`)} aria-haspopup="menu" aria-expanded={openNoteMenuId === note.id} onClick={() => setOpenNoteMenuId(current => current === note.id ? null : note.id)}>⋮</button>
                  {openNoteMenuId === note.id && <div className="bible-notes-menu-items" role="menu">
                    <button type="button" role="menuitem" onClick={() => edit(note)}>{translate('編輯')}</button>
                    <button type="button" role="menuitem" onClick={() => remove(note)}>{translate('刪除')}</button>
                  </div>}
                </div>
                <small>{translate(noteReference(note))} · {new Date(note.updatedAt).toLocaleDateString(language === 'S' ? 'zh-CN' : 'zh-TW')}</small>
                {note.title && <strong>{note.title}</strong>}
                <p>{note.content}</p>
                {note.tags && <small>#{note.tags}</small>}
              </article>
            ))}
          </div>
        </div>}
        <footer className="bible-note-dialog-footer"><button type="button" onClick={() => { onClose(); navigate('/bible-notes'); }}>{translate('查看全部筆記')}</button><button type="button" onClick={onClose}>{translate('關閉')}</button></footer>
      </section>
    </div>, document.body
  );
}
